import {defineStore} from 'pinia';
import {send} from "@/composables/apiClient";
import router from "@/router";
import {useSubscriptionStore} from "@/stores/subscriptionStore";
import {useOnboardingStore} from "@/stores/onboardingStore";

const SSO_COOKIE_NAME = 'sso_token';
const STORAGE_KEY = 'auth';

// Bearer token held in module memory only -- never written to localStorage.
// On page reload this is null and the httpOnly sso_token cookie takes over
// (Authenticate.php only injects the cookie when no Authorization header is
// present, so the cookie never clobbers an in-memory token).
let _sessionToken = null;
export function getSessionToken() { return _sessionToken; }

/**
 * Reviver: parse localStorage string into an object, or null if
 * missing / malformed. Quietly swallows parse errors so a corrupt
 * entry doesn't brick the app - we just treat it as no session.
 */
function loadFromStorage() {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        // Remove any token that was persisted by a previous version of this
        // code -- tokens must not live in localStorage (XSS exposure).
        if (parsed && 'token' in parsed) {
            delete parsed.token;
            localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
        }
        return parsed;
    } catch {
        return null;
    }
}

/**
 * Persist the object back to localStorage as a string.
 */
function persist(authObject) {
    if (authObject === null) {
        localStorage.removeItem(STORAGE_KEY);
        return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(authObject));
}

export const useAuthStore = defineStore('authStore', {
    // State now holds the PARSED object, not the raw JSON string.
    // Previously every getter (user, menus, portals, configs, consents)
    // called JSON.parse(state.auth) - 5 parses × every component
    // access × every re-render. Parse once at store init and after
    // each write; getters become plain property reads.
    state: () => ({
        auth: loadFromStorage(),
        // True from the moment logout starts until the session is gone. The
        // logout request has to be sent with the session still valid, so auth
        // stays populated for a whole round trip - long enough for a listener
        // or a finished request to decide the user belongs somewhere else and
        // navigate there, against the redirect to login.
        loggingOut: false,
    }),

    getters: {
        user: (state) => state.auth?.user ?? null,
        menus: (state) => state.auth?.menus ?? null,
        portals: (state) => state.auth?.portals ?? null,
        configs: (state) => state.auth?.configs ?? null,
        consents: (state) => {
            const c = state.auth?.consents;
            return Array.isArray(c) && c.length > 0 ? c : null;
        },
    },

    actions: {
        login(authData) {
            _sessionToken = authData.token ?? null;
            this.auth = {
                user: authData.user,
                menus: authData.menus,
                portals: authData.portals,
                configs: authData.settings,
                consents: authData.consents,
                // token intentionally excluded -- held in _sessionToken (memory only)
            };
            persist(this.auth);
            this.loggingOut = false;

            try {
                useOnboardingStore().loadForUser(authData.user?.id);
            } catch (e) {}

            if (authData.subscriptionStatus) {
                const subscriptionStore = useSubscriptionStore();
                subscriptionStore.setStatus(authData.subscriptionStatus);
            }

            return Promise.resolve(authData.user);
        },

        isAuthenticated() {
            return !!this.auth && !!this.user && this.user?.email_verified_at !== null;
        },

        updateAuthUser(user) {
            if (!this.auth) return;
            this.auth = {...this.auth, user};
            persist(this.auth);
        },

        updateAuthCompany(company) {
            if (!this.auth?.user) return;
            this.auth = {
                ...this.auth,
                user: {...this.auth.user, company: {...this.auth.user.company, ...company}},
            };
            persist(this.auth);
        },

        updateAuthConfig(configs) {
            if (!this.auth) return;
            this.auth = {...this.auth, configs};
            persist(this.auth);
        },

        updateConsents(consents) {
            if (!this.auth) return;
            this.auth = {...this.auth, consents};
            persist(this.auth);
        },

        updateAuth(data) {
            if (!this.auth) return;
            const next = {...this.auth};
            if (data.user) next.user = data.user;
            if (data.menus) next.menus = data.menus;
            if (data.portals) next.portals = data.portals;
            if (data.settings) next.configs = data.settings;
            if ('consents' in data) next.consents = data.consents;
            this.auth = next;
            persist(this.auth);

            if (data.subscriptionStatus) {
                const subscriptionStore = useSubscriptionStore();
                subscriptionStore.setStatus(data.subscriptionStatus);
            }
        },

        async logout() {
            this.loggingOut = true;
            try {
                if (this.auth) {
                    await send('post', `logout`);
                }
            } catch (error) {
                console.error('Logout failed:', error);
            } finally {
                this.unsetAuth();
                // The router instance, not useNavigation(): that calls
                // useRouter(), which is inject() based and returns undefined
                // outside a component setup. In here it silently did nothing,
                // so logging out cleared the session and left the user sitting
                // on the page. Everywhere with a live poll masked it - the next
                // 401 redirected - but a static page like the setup wizard just
                // sat there still looking signed in.
                if (router.currentRoute.value.name !== 'login') {
                    router.push({name: 'login'});
                }
            }
        },
        unsetAuth() {
            _sessionToken = null;
            this.auth = null;
            persist(null);
            document.cookie = `${SSO_COOKIE_NAME}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
            try {
                const subscriptionStore = useSubscriptionStore();
                subscriptionStore.clear();
            } catch (e) {}
            try {
                const onboardingStore = useOnboardingStore();
                onboardingStore.reset();
            } catch (e) {}
        },
    },
});
